import { useId, useRef, useState } from 'react';
import type {
  ReportParameterDefinition,
  ReportParameterValidValue,
  ReportParameterValue,
  ResolvedParameterValues
} from '@/types';
import { DateField } from './DateField';
import { SearchablePicker } from './SearchablePicker';
import { ReportDefaultPanel } from './ReportDefaultPanel';
import { defaultAffordanceLabel, describeDefault } from './reportDefault';
import type { LookupState } from './useResolvedValues';
import { includesValue, optionFor, selectedOptionFor, unmatchedLabel, withoutValue } from './valueMatching';

/**
 * Above this, a plain dropdown stops being something a person can actually use.
 *
 * It was 60, which was too tight by roughly an order of magnitude and is what made this look
 * broken. "Any Client Detail (Custom View)" resolves 96 Views, 9 Countries, 10 Date Filters and 8
 * Products; only View crossed 60, so the one field a user is most likely to touch turned into a
 * type-to-filter text box while everything around it was a dropdown — and a text box you have to
 * type into to discover its contents is, from the far side of the screen, indistinguishable from
 * the free-text box this work replaced.
 *
 * A native select handles a few hundred entries perfectly well: it is scrollable, keyboard
 * type-ahead works, and it shows what is available without being asked. 300 keeps every list on
 * that report native and still routes the one genuinely unusable list — Head Office, at 5,844 —
 * to the searchable picker, which is what that control exists for.
 */
const MAX_PLAIN_DROPDOWN = 300;

/**
 * The same question for a multi-valued parameter, which renders as a wall of checkboxes rather
 * than a dropdown. A wall is fine at ten and unreadable at three hundred, so it turns over to the
 * searchable picker — which multi-selects into chips — much sooner.
 */
const MAX_CHECKBOX_LIST = 40;

/**
 * Beyond this many checkboxes the wall wants the whole card, not half of it.
 *
 * Below it a handful of short labels sit comfortably in one column beside another field, which is
 * the case that pays for the two-column layout.
 */
const CHECKBOXES_NEEDING_FULL_WIDTH = 4;

export function ParameterField({
  def,
  values,
  fromField,
  resolved,
  lookupState,
  seededWith,
  onUseSeededValue,
  defaultEchoed,
  reportPath,
  upstream,
  dependencyLabels,
  onChange,
  disabled
}: {
  def: ReportParameterDefinition;
  values: string[];
  fromField: string | null;
  resolved: ResolvedParameterValues | undefined;
  /**
   * How far this parameter's own lookup has got. `failed` covers a lookup that came back empty
   * across the board as well as one that errored, so an absent list says nothing about this
   * parameter — see LookupState.
   */
  lookupState: LookupState;
  /** The account this parameter's value list is being resolved as, when the user did not set it. */
  seededWith?: string;
  /**
   * Stores {@link seededWith} as this parameter's value. Absent where that would be wrong to
   * offer — see the ownership note in ParametersEditor for which cases those are, and why an
   * existing subscription is repaired on request rather than on save.
   */
  onUseSeededValue?: () => void;
  /** This parameter's own value was sent upstream, so any "default" it came back with is that. */
  defaultEchoed: boolean;
  reportPath?: string;
  upstream: ReportParameterValue[];
  /** The parameters this one's list is waiting on and nothing has supplied, named as a reader
   *  sees them on the form. */
  dependencyLabels: string[];
  onChange: (v: string[]) => void;
  disabled: boolean;
}) {
  const uid = useId();
  const controlId = `${uid}-control`;
  const panelId = `${uid}-default`;
  const [showDefault, setShowDefault] = useState(false);

  const single = values[0] ?? '';

  // The report server's answer wins over the catalog's cached list: it was resolved against the
  // choices actually made, where the catalog's was cached against whatever was current when the
  // report was last published. An EMPTY resolved list is not an answer though — the server now
  // also replies about parameters it resolved a default but no list for, and treating that as
  // "the list is empty" would throw away a perfectly good catalogue list for anything that has
  // both.
  const options: ReportParameterValidValue[] =
    (resolved?.validValues?.length ? resolved.validValues : def.validValues) ?? [];
  const hasOptions = options.length > 0;
  const total = resolved?.validValues?.length ? resolved.totalCount : options.length;
  const limit = def.multiValued ? MAX_CHECKBOX_LIST : MAX_PLAIN_DROPDOWN;
  const needsSearch = Boolean(resolved?.truncated) || total > limit;
  const labelFor = useRememberedLabel(options);

  /**
   * Whether the report answers this parameter from a LIST, whatever type it declares.
   *
   * The two are independent in SSRS and this report proves it: "Scope Version" on /Sales/Scope 3 is
   * declared DateTime and has nine valid values, of which the one a user actually wants is labelled
   * "Rates at time of travel" (value 1900-01-01). Choosing the control from the type gave it a date
   * picker showing 01/01/1900 — a control that cannot express the choice at all, and that quietly
   * invites someone to pick a date that is not one of the nine the report accepts. Native PBIRS
   * renders it as a dropdown, because a value list beats a declared type.
   *
   * Deliberately NOT keyed on `dependencies`. Depending on another parameter says where this one's
   * default or list is computed FROM, not that it has a list: FromDate and ToDate both depend on
   * DefaultDatePeriod and are real dates with no list at all, and treating them as list-valued
   * would take away the date picker that is right for them.
   */
  const expectsList = def.dynamicValidValues || (def.validValues?.length ?? 0) > 0;

  const reportDefault = describeDefault(def, resolved, defaultEchoed);
  const affordance = fromField ? null : defaultAffordanceLabel(reportDefault);

  // Waiting on a list, with nothing yet to show. This is the case that used to render as a plain
  // text box holding the raw stored id — not a slower version of the right control but a different
  // and wrong one, which the user reasonably read as the form throwing their choice away. It gets
  // a control of its own below, which beats an empty picker and beats a free-text box that turns
  // into a picker under the cursor.
  const awaitingList = lookupState === 'resolving' && !hasOptions;

  // Waiting, but with the previous answer still on screen — what keepPreviousData buys. The
  // control stays exactly as it was; only a quiet marker says another answer is coming.
  const refreshing = lookupState === 'resolving' && hasOptions;

  // A wall of checkboxes and a searchable picker with its dropdown both want the full card width;
  // so does a query-bound field, whose whole content is a sentence. Everything else halves.
  const isCheckboxWall = hasOptions && !needsSearch && def.multiValued;
  const wide = needsSearch
    || Boolean(fromField)
    || (isCheckboxWall && options.length > CHECKBOXES_NEEDING_FULL_WIDTH);

  // PBIRS leaves Prompt empty on a parameter the report fills in itself, and the API passes that
  // straight through — which rendered a field with no label at all. The name is not pretty but it
  // is what the report calls it, and it beats a nameless control.
  const title = def.displayName.trim() === '' ? def.name : def.displayName;

  const meta = [
    def.type,
    def.multiValued ? 'multiple' : null,
    def.promptUser ? null : 'set by the report'
  ].filter(Boolean).join(' · ');

  // A checkbox wall has no single control for a <label> to point at, so it gets a labelled group
  // instead. Everything else keeps a real, clickable label.
  const labelled = !isCheckboxWall;

  return (
    <div className={`field param-field${wide ? ' is-wide' : ''}`}>
      <div className="param-label-row">
        {labelled
          ? <label htmlFor={controlId}>{title}</label>
          : <span className="param-label" id={controlId}>{title}</span>}
        <span className="param-meta">{meta}</span>
        {/* A control cannot show that a fresher answer is on its way, and this is the one place
            that fact belongs: the list below stays usable throughout. */}
        {refreshing && <span className="param-refreshing">rechecking...</span>}
        {affordance && (
          <button
            type="button"
            className="param-info"
            aria-expanded={showDefault}
            aria-controls={panelId}
            title={`What ${title} does if nothing is set here`}
            onClick={() => setShowDefault(v => !v)}>
            {affordance}
          </button>
        )}
      </div>

      {hasOptions ? (
        needsSearch ? (
          <SearchablePicker
            id={controlId}
            def={def}
            options={options}
            total={total}
            truncated={Boolean(resolved?.truncated)}
            values={values}
            rememberedLabel={labelFor}
            reportPath={reportPath}
            upstream={upstream}
            onChange={onChange}
            disabled={disabled}
          />
        ) : def.multiValued ? (
          <div className="param-choices" role="group" aria-labelledby={controlId}>
            {options.map(v => (
              <label key={v.value} className="param-choice">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={includesValue(values, v.value)}
                  onChange={e =>
                    onChange(
                      e.target.checked
                        ? [...values, v.value]
                        : withoutValue(values, v.value)
                    )
                  }
                />
                {v.label}
              </label>
            ))}
            {/* A stored value the report no longer offers still has to be visible and removable
                here, the same reason the single-valued select keeps its own option for one.
                Without this a checkbox wall had nothing to show for it at all: no box, no code,
                no way to tell the value was still on the subscription until the next save
                silently dropped it. */}
            {values.filter(v => v !== '' && !optionFor(v, options)).map(v => (
              <label key={v} className="param-choice">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked
                  onChange={() => onChange(withoutValue(values, v))}
                />
                {unmatchedLabel(v)}
              </label>
            ))}
          </div>
        ) : (
          <select
            id={controlId}
            className="select"
            disabled={disabled}
            // The MATCHED option's value, not the raw stored string, and the two differ for a date
            // stored in the en-US spelling (see selectedOptionFor). A <select> whose value matches
            // no <option> does not render as empty — the browser falls back to showing the FIRST
            // entry, so binding the raw string here while the synthetic option below had been
            // suppressed would put a wrong ScopeVersion on screen looking deliberately chosen.
            //
            // This changes nothing about what is stored: onChange only fires when the user picks
            // something, so a subscription opened and closed keeps the exact string it had.
            value={selectedOptionFor(single, options)?.value ?? single}
            onChange={e => onChange([e.target.value])}>
            {/* A value the report no longer offers still has to be visible, or the field would
                silently switch the subscription to a different one on the next save. Matched with
                selectedOptionFor rather than a raw === so a value that is genuinely still there —
                spelled with different incidental whitespace, or as an en-US date rather than a
                day-first one — keeps its friendly label instead of falling back to this. */}
            {single !== '' && !selectedOptionFor(single, options) && (
              <option value={single}>{unmatchedLabel(single)}</option>
            )}
            {(def.nullable || def.allowBlank) && <option value="">(none)</option>}
            {/* Nothing stored and no blank option to hold the empty value: without this the
                browser falls back to displaying the first entry, so Country Code read "BE" on a
                subscription that stores no country and would run without one. Disabled, so it can
                only ever be shown — clearing the field stays the job of the default panel. */}
            {single === '' && !(def.nullable || def.allowBlank) && (
              <option value="" disabled>— not set —</option>
            )}
            {options.map(v => (
              <option key={v.value} value={v.value}>{v.label}</option>
            ))}
          </select>
        )
      ) : awaitingList ? (
        <ResolvingField values={values} labelFor={labelFor} />
      ) : /* The type-derived controls below are only ever right for a parameter that answers from
             its type rather than from a list. Where a list is expected but has not arrived, the
             free-text box at the bottom is the honest fallback: it can hold any of the values the
             report would have offered, where a date or number control silently cannot. */
        !expectsList && def.type === 'Boolean' ? (
        <div className="param-choice">
          <input
            id={controlId}
            type="checkbox"
            disabled={disabled}
            checked={single.toLowerCase() === 'true'}
            onChange={e => onChange([e.target.checked ? 'True' : 'False'])}
          />
          <span>{single.toLowerCase() === 'true' ? 'Yes' : 'No'}</span>
        </div>
      ) : !expectsList && def.type === 'DateTime' && !def.multiValued ? (
        <DateField
          id={controlId}
          value={single}
          onChange={onChange}
          disabled={disabled}
          allowEmpty={def.nullable || def.allowBlank}
        />
      ) : !expectsList && (def.type === 'Integer' || def.type === 'Float') && !def.multiValued ? (
        <input
          id={controlId}
          className="input"
          type="number"
          step={def.type === 'Integer' ? 1 : 'any'}
          disabled={disabled}
          value={single}
          // Deliberately no placeholder. Putting the report's default inside the box renders it in
          // the same spot, and very nearly the same grey, as a value that IS stored — which is the
          // one confusion this field cannot afford. The default lives behind the label row.
          onChange={e => onChange(e.target.value === '' ? [] : [e.target.value])}
        />
      ) : def.multiValued ? (
        <input
          id={controlId}
          className="input"
          disabled={disabled}
          value={values.join(', ')}
          placeholder="Comma-separated values"
          onChange={e =>
            onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))
          }
        />
      ) : (
        <input
          id={controlId}
          className="input"
          disabled={disabled}
          value={single}
          // See the number field above: the default is never shown inside the box.
          onChange={e => onChange([e.target.value])}
        />
      )}

      {/*
        Seeding is a decision the reader is entitled to see, and it is not one a control can show:
        the list beside it was resolved as somebody, and that somebody is not stored anywhere.

        And "not stored anywhere" is the fault, not a reassurance, which is what this note used to
        make it sound like. A subscription that leaves this unset resolves it at run time as the
        portal's service account, whose Country Code list on /Sales/Scope 3 has 0 entries — so
        every value the lists below offered is rejected on the next run with "not a valid value"
        and no email is sent. Hence the repair beside it, and hence the plain statement of what
        pressing it costs: a subscription pinned to one person's access is a subscription that
        breaks when they leave, which is exactly what will happen to the person-owned ones already
        on this report.
      */}
      {seededWith && (
        <div className="param-note">
          Lists are being looked up as <code>{seededWith}</code> — this report scopes them to a
          user, and the portal reaches the report server as a service account that belongs to none.
          Nothing is stored here, so when this subscription runs the report resolves {title} as
          that service account instead, and the run fails rather than sending.
          {onUseSeededValue && (
            <div style={{ marginTop: 6 }}>
              <button className="btn-secondary btn-sm" type="button" onClick={onUseSeededValue}>
                Set {title} to {seededWith}
              </button>
              <div style={{ marginTop: 4 }}>
                Saving that fixes the runs, and pins them: the report is then read with that
                person&rsquo;s access for good, everyone on the recipient list gets what{' '}
                <em>they</em> can see, and it breaks again if they leave Key Travel.
              </div>
            </div>
          )}
        </div>
      )}

      {/*
        What is left under a control, and why each one stayed.
        · a query-bound value: the control is disabled and cannot say what filled it in
        · a list that should be here and is not: says why the field is empty, and what to do

        That second one splits three ways, and the split is the point. A lookup that did not happen
        must not be described as a report with nothing to offer — the endpoint answers
        200-with-nothing for an unreachable server and for a genuine empty alike, so blaming the
        report sends the reader off to check choices of their own that were never at fault. Only
        once the lookup is known to have worked is it fair to say the cascade is waiting on an
        upstream choice, or that the report simply has no values here.

        Everything else that used to sit under a control said something the control already showed.
      */}
      {fromField ? (
        <div className="param-note">
          Taken from the <code>{fromField}</code> column of this subscription&rsquo;s own query, so it
          differs per recipient and cannot be set here.
        </div>
      ) : !hasOptions && !awaitingList && expectsList ? (
        <div className="param-note">
          {lookupState === 'failed'
            ? 'The allowed values could not be looked up just now — the report server returned nothing for this report. Type the value you want.'
            : dependencyLabels.length > 0
              ? `Available once ${joinList(dependencyLabels)} ${dependencyLabels.length > 1 ? 'are' : 'is'} chosen.`
              : 'The report offered no values for this — type the one you want.'}
        </div>
      ) : null}

      {showDefault && (
        <ReportDefaultPanel
          id={panelId}
          info={reportDefault}
          stored={values}
          options={options}
          parameterType={def.type}
          rememberedLabel={labelFor}
          onReset={() => onChange([])}
          disabled={disabled}
        />
      )}
    </div>
  );
}

/**
 * A parameter whose list is still being fetched.
 *
 * Deliberately not an input. The whole fault this replaces was that a field mid-lookup rendered as
 * an editable text box holding the raw stored id: on /Sales/Scope 3 the Country Code dropdown
 * reading "UK" became a text box reading "1" for the 1.6s the next lookup took, which reads as the
 * form discarding the choice just made. A busy, obviously-inert control cannot be mistaken for
 * either a free-text box or a finished picker, and the current selection stays visible beside it —
 * by its label, which is remembered across the gap, not by the id underneath it.
 */
function ResolvingField({
  values,
  labelFor
}: {
  values: string[];
  labelFor: (value: string) => string | undefined;
}) {
  return (
    <div className="param-resolving" role="status" aria-live="polite" aria-busy="true">
      <span className="param-spinner" aria-hidden="true" />
      <span>Looking up the allowed values...</span>
      {/* Labelled, because on a first load no list has been seen yet and this can only show the
          stored id. "Currently: 1" is a fact about the subscription; a bare "1" beside a spinner
          is just a number, which is how the raw-value box read in the first place. */}
      {values.length > 0 && (
        <span className="param-chips">
          <span className="param-resolving-label">Currently:</span>
          {values.map(v => <span key={v} className="param-chip">{labelFor(v) ?? v}</span>)}
        </span>
      )}
    </div>
  );
}

/**
 * Remembers the label a value was last seen with.
 *
 * A selected value outlives the list it came from: a new answer may not contain the old value at
 * all, and on a first load the list has not arrived yet. Without this, "United Kingdom" degrades to
 * "1" the moment either happens — the value is intact, but the user is shown a raw id where a name
 * was, which is indistinguishable from the form having reset itself.
 *
 * Returns `undefined`, not the raw value, when nothing has been seen: that is what lets a caller
 * tell "genuinely never resolved" apart from "resolved to a label identical to its value", which
 * matters once the caller's own fallback is unmatchedLabel rather than the value itself. Keyed
 * with the same trimmed comparison as optionFor, so a value remembered from one render matches
 * the same value looked up on another even if incidental whitespace differs between the two.
 */
function useRememberedLabel(options: ReportParameterValidValue[]) {
  const seen = useRef(new Map<string, string>());
  for (const o of options) seen.current.set(o.value.trim(), o.label);

  return (value: string) => seen.current.get(value.trim());
}

function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
